'use client';

import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  Check,
  Download,
  Eye,
  FileCode2,
  Github,
  History,
  Palette,
  RefreshCw,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useLivingDocsPanelClose } from '@/contexts/LivingDocsPanelContext';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import {
  SidePanelResizeHandle,
  useSidePanelSize,
} from '@/components/core/app-layout/SidePanelSize';
import { useAuth } from '@/app/auth-provider';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { SafeMarkdown } from '@/components/shared/SafeMarkdown';
import {
  PROJECT_DESIGN_SYSTEM_UPDATED_EVENT,
  type ProjectDesignSystemDetail,
  type ProjectDesignSystemFinding,
  type ProjectDesignSystemLintSummary,
  type ProjectDesignSystemSource,
  type ProjectDesignSystemTokenSnapshot,
} from '@/lib/project-design-system/shared';

type PanelMode = 'preview' | 'source' | 'history';
type Validation = {
  summary: ProjectDesignSystemLintSummary;
  findings: ProjectDesignSystemFinding[];
  tokenSnapshot: ProjectDesignSystemTokenSnapshot;
};

export function WorkspaceDesignSystemPanel({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sourceRef = useRef<HTMLTextAreaElement>(null);
  const loadGenerationRef = useRef(0);
  const [detail, setDetail] = useState<ProjectDesignSystemDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [source, setSource] = useState<ProjectDesignSystemSource>('manual');
  const [mode, setMode] = useState<PanelMode>('preview');
  const [validation, setValidation] = useState<Validation | null>(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  const { panelWidthValue: sharedPanelWidth } = useSidePanelSize();
  const currentContent = detail?.current?.content || '';
  const dirty = draft !== currentContent;

  const authorizedFetch = useCallback(
    async (url: string, init: RequestInit = {}) => {
      if (!user) throw new Error('Sign in to edit DESIGN.md');
      const token = await user.getIdToken();
      return fetch(url, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init.body ? { 'Content-Type': 'application/json' } : {}),
          ...(init.headers || {}),
        },
      });
    },
    [user]
  );

  const load = useCallback(async () => {
    if (!user || !projectId) return;
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    try {
      const response = await authorizedFetch(
        `/api/projects/${encodeURIComponent(projectId)}/design-system`
      );
      const payload = (await response.json().catch(() => ({}))) as {
        designSystem?: ProjectDesignSystemDetail | null;
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || 'Failed to load DESIGN.md');
      if (generation !== loadGenerationRef.current) return;
      const next = payload.designSystem || null;
      setDetail(next);
      setDraft(next?.current?.content || '');
      setValidation(
        next?.current
          ? {
              summary: next.current.lintSummary,
              findings: next.current.findings,
              tokenSnapshot: next.current.tokenSnapshot,
            }
          : null
      );
    } catch (error) {
      if (generation === loadGenerationRef.current) {
        toast.error(error instanceof Error ? error.message : 'Failed to load DESIGN.md');
      }
    } finally {
      if (generation === loadGenerationRef.current) setLoading(false);
    }
  }, [authorizedFetch, projectId, user]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [load, open]);

  useEffect(() => {
    if (!open) return;
    const handleUpdate = () => {
      if (!dirty) void load();
    };
    window.addEventListener(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT, handleUpdate);
    return () => window.removeEventListener(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT, handleUpdate);
  }, [dirty, load, open]);

  useEffect(() => {
    if (!open || !draft.trim() || draft === currentContent) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setValidating(true);
      void authorizedFetch(
        `/api/projects/${encodeURIComponent(projectId)}/design-system/validate`,
        {
          method: 'POST',
          body: JSON.stringify({ content: draft }),
          signal: controller.signal,
        }
      )
        .then(async response => {
          const payload = (await response.json().catch(() => ({}))) as {
            validation?: Validation;
            error?: string;
          };
          if (!response.ok || !payload.validation) {
            throw new Error(payload.error || 'Failed to validate DESIGN.md');
          }
          setValidation(payload.validation);
        })
        .catch(error => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setValidation({
            summary: { errors: 1, warnings: 0, infos: 0 },
            findings: [
              {
                severity: 'error',
                path: null,
                rule: 'request',
                message: error instanceof Error ? error.message : 'Validation failed',
              },
            ],
            tokenSnapshot: emptyTokenSnapshot(),
          });
        })
        .finally(() => setValidating(false));
    }, 500);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [authorizedFetch, currentContent, draft, open, projectId]);

  const save = useCallback(async () => {
    if (!draft.trim() || saving || validating || validation?.summary.errors) return;
    setSaving(true);
    try {
      const response = await authorizedFetch(
        `/api/projects/${encodeURIComponent(projectId)}/design-system`,
        {
          method: 'PUT',
          body: JSON.stringify({
            content: draft,
            expectedVersion: detail?.currentVersion || 0,
            source: source === 'upload' || source === 'paste' ? source : 'manual',
            idempotencyKey: `design-system-ui-${crypto.randomUUID()}`,
          }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        detail?: ProjectDesignSystemDetail;
        validation?: Validation;
        error?: string;
      };
      if (!response.ok || !payload.detail) {
        if (payload.validation) setValidation(payload.validation);
        throw new Error(payload.error || 'Failed to save DESIGN.md');
      }
      setDetail(payload.detail);
      setDraft(payload.detail.current?.content || '');
      setSource('manual');
      toast.success(`DESIGN.md version ${payload.detail.currentVersion} saved`);
      window.dispatchEvent(new Event(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save DESIGN.md');
    } finally {
      setSaving(false);
    }
  }, [
    authorizedFetch,
    detail?.currentVersion,
    draft,
    projectId,
    saving,
    source,
    validating,
    validation,
  ]);

  const importFromGitHub = useCallback(async () => {
    if (dirty && !window.confirm('Discard the unsaved source and import DESIGN.md from GitHub?')) {
      return;
    }
    setImporting(true);
    try {
      const response = await authorizedFetch(
        `/api/projects/${encodeURIComponent(projectId)}/design-system/import/github`,
        {
          method: 'POST',
          body: JSON.stringify({
            expectedVersion: detail?.currentVersion || 0,
            idempotencyKey: `design-system-github-${crypto.randomUUID()}`,
          }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        detail?: ProjectDesignSystemDetail;
        error?: string;
      };
      if (!response.ok || !payload.detail) {
        throw new Error(payload.error || 'Failed to import DESIGN.md');
      }
      setDetail(payload.detail);
      setDraft(payload.detail.current?.content || '');
      setMode('preview');
      setValidation(
        payload.detail.current
          ? {
              summary: payload.detail.current.lintSummary,
              findings: payload.detail.current.findings,
              tokenSnapshot: payload.detail.current.tokenSnapshot,
            }
          : null
      );
      toast.success('DESIGN.md imported from GitHub');
      window.dispatchEvent(new Event(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to import DESIGN.md');
    } finally {
      setImporting(false);
    }
  }, [authorizedFetch, detail?.currentVersion, dirty, projectId]);

  const restore = useCallback(
    async (version: number) => {
      if (dirty && !window.confirm('Discard the unsaved source and restore this version?')) return;
      setRestoringVersion(version);
      try {
        const response = await authorizedFetch(
          `/api/projects/${encodeURIComponent(projectId)}/design-system/versions/${version}/restore`,
          {
            method: 'POST',
            body: JSON.stringify({
              expectedVersion: detail?.currentVersion || 0,
              idempotencyKey: `design-system-restore-${version}-${crypto.randomUUID()}`,
            }),
          }
        );
        const payload = (await response.json().catch(() => ({}))) as {
          detail?: ProjectDesignSystemDetail;
          error?: string;
        };
        if (!response.ok || !payload.detail) {
          throw new Error(payload.error || 'Failed to restore DESIGN.md');
        }
        setDetail(payload.detail);
        setDraft(payload.detail.current?.content || '');
        setMode('preview');
        setValidation(
          payload.detail.current
            ? {
                summary: payload.detail.current.lintSummary,
                findings: payload.detail.current.findings,
                tokenSnapshot: payload.detail.current.tokenSnapshot,
              }
            : null
        );
        toast.success(`Restored version ${version} as version ${payload.detail.currentVersion}`);
        window.dispatchEvent(new Event(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to restore DESIGN.md');
      } finally {
        setRestoringVersion(null);
      }
    },
    [authorizedFetch, detail?.currentVersion, dirty, projectId]
  );

  const toggleEnabled = useCallback(async () => {
    if (!detail?.currentVersion) return;
    try {
      const response = await authorizedFetch(
        `/api/projects/${encodeURIComponent(projectId)}/design-system`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            enabled: !detail.enabled,
            expectedVersion: detail.currentVersion,
          }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        designSystem?: ProjectDesignSystemDetail;
        error?: string;
      };
      if (!response.ok || !payload.designSystem) {
        throw new Error(payload.error || 'Failed to update DESIGN.md');
      }
      setDetail(payload.designSystem);
      window.dispatchEvent(new Event(PROJECT_DESIGN_SYSTEM_UPDATED_EVENT));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update DESIGN.md');
    }
  }, [authorizedFetch, detail, projectId]);

  const download = useCallback(async () => {
    try {
      const response = await authorizedFetch(
        `/api/projects/${encodeURIComponent(projectId)}/design-system/download`
      );
      if (!response.ok) throw new Error('Failed to download DESIGN.md');
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'DESIGN.md';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download DESIGN.md');
    }
  }, [authorizedFetch, projectId]);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Discard unsaved DESIGN.md changes?')) return;
    onOpenChange(false);
  }, [dirty, onOpenChange]);

  const refresh = useCallback(() => {
    if (dirty && !window.confirm('Discard unsaved DESIGN.md changes and reload?')) return;
    void load();
  }, [dirty, load]);

  const paste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) throw new Error('Clipboard is empty');
      setDraft(text);
      setSource('paste');
      setMode('source');
      queueMicrotask(() => sourceRef.current?.focus());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to read the clipboard');
    }
  }, []);

  const previewTokens = validation?.tokenSnapshot || detail?.current?.tokenSnapshot;
  const previewMarkdown = useMemo(
    () => previewTokens?.sections.map(section => section.content).join('\n\n') || '',
    [previewTokens]
  );
  useLivingDocsPanelClose(requestClose, open);

  if (!open) return null;

  return (
    <Flex
      as="aside"
      aria-label="Project design system"
      data-section="workspaceDesignSystemPanel"
      position="relative"
      direction="column"
      w={sharedPanelWidth}
      minW={sharedPanelWidth}
      h="full"
      minH={0}
      flexShrink={0}
      borderLeftWidth="1px"
      borderColor="ui.border.subtle"
      bg="ui.surface.rail"
      boxShadow="var(--attune-shadow-elevated, none)"
    >
      <SidePanelResizeHandle label="Resize DESIGN.md editor" />
      <HStack
        minH="54px"
        px="3"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        bg="ui.surface.rail"
        justify="space-between"
        flexShrink={0}
      >
        <HStack gap="2" minW={0}>
          <Palette size={17} aria-hidden="true" />
          <Text fontSize="13px" fontWeight="550" truncate>
            DESIGN.md
          </Text>
          {detail?.currentVersion ? (
            <Badge size="sm" colorPalette={detail.enabled ? 'green' : 'gray'}>
              {detail.enabled ? `v${detail.currentVersion}` : 'paused'}
            </Badge>
          ) : null}
        </HStack>
        <HStack gap="1">
          <IconButton
            aria-label="Refresh DESIGN.md"
            title="Refresh"
            size="sm"
            variant="ghost"
            onClick={refresh}
          >
            <RefreshCw size={15} />
          </IconButton>
          <IconButton
            aria-label="Download DESIGN.md"
            title="Download"
            size="sm"
            variant="ghost"
            disabled={!detail?.current}
            onClick={() => void download()}
          >
            <Download size={15} />
          </IconButton>
          <SidePanelToggleButton open label="Close DESIGN.md" onClick={requestClose} />
        </HStack>
      </HStack>

      <HStack
        px="3"
        py="2"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        justify="space-between"
      >
        <HStack gap="1">
          {(['preview', 'source', 'history'] as const).map(value => (
            <Button
              key={value}
              size="xs"
              variant={mode === value ? 'subtle' : 'ghost'}
              onClick={() => setMode(value)}
            >
              {value === 'preview' ? (
                <Eye size={14} />
              ) : value === 'source' ? (
                <FileCode2 size={14} />
              ) : (
                <History size={14} />
              )}
              {value[0].toUpperCase() + value.slice(1)}
            </Button>
          ))}
        </HStack>
        {detail?.current ? (
          <Button size="xs" variant="ghost" onClick={() => void toggleEnabled()}>
            {detail.enabled ? 'Pause guidance' : 'Enable guidance'}
          </Button>
        ) : null}
      </HStack>

      <Box flex="1" minH={0} overflowY="auto" bg="ui.surface.rail">
        {loading ? (
          <Center h="full">
            <Spinner size="md" />
          </Center>
        ) : mode === 'source' ? (
          <Flex direction="column" h="full" minH={0} p="4" gap="3">
            <HStack gap="1">
              <Button size="xs" variant="ghost" onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} /> Upload
              </Button>
              <Button size="xs" variant="ghost" onClick={() => void paste()}>
                <FileCode2 size={14} /> Paste
              </Button>
              <Button
                size="xs"
                variant="ghost"
                loading={importing}
                onClick={() => void importFromGitHub()}
              >
                <Github size={14} /> Import GitHub
              </Button>
            </HStack>
            <Textarea
              ref={sourceRef}
              aria-label="DESIGN.md source"
              value={draft}
              onChange={event => {
                setDraft(event.target.value);
                setSource('manual');
              }}
              onKeyDown={event => {
                if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
                  event.preventDefault();
                  void save();
                }
              }}
              placeholder="Paste or write DESIGN.md…"
              flex="1"
              minH="320px"
              resize="none"
              fontFamily="mono"
              fontSize="12px"
              lineHeight="1.65"
              bg="ui.surface.drawer"
            />
            <HStack justify="space-between" align="start">
              <Box minW={0}>
                <Text
                  fontSize="xs"
                  color={validation?.summary.errors ? 'status.danger.fg' : 'ui.text.secondary'}
                >
                  {validating
                    ? 'Validating…'
                    : validation
                      ? `${validation.summary.errors} errors · ${validation.summary.warnings} warnings`
                      : 'Official DESIGN.md validation runs before save.'}
                </Text>
                {validation?.findings.slice(0, 3).map((finding, index) => (
                  <Text
                    key={`${finding.rule}-${index}`}
                    mt="1"
                    fontSize="2xs"
                    color={finding.severity === 'error' ? 'status.danger.fg' : 'ui.text.tertiary'}
                  >
                    {finding.path ? `${finding.path}: ` : ''}
                    {finding.message}
                  </Text>
                ))}
              </Box>
              <Button
                size="sm"
                loading={saving}
                disabled={
                  !dirty || validating || !draft.trim() || Boolean(validation?.summary.errors)
                }
                onClick={() => void save()}
              >
                <Check size={15} /> Save version
              </Button>
            </HStack>
          </Flex>
        ) : mode === 'history' ? (
          <VStack align="stretch" gap="2" p="4">
            {(detail?.versions || []).map(version => (
              <HStack
                key={version.id}
                p="3"
                borderWidth="1px"
                borderColor="ui.border.subtle"
                borderRadius="glassButton"
                bg="ui.surface.drawer"
                justify="space-between"
              >
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="700">
                    Version {version.version}
                  </Text>
                  <Text fontSize="xs" color="ui.text.tertiary">
                    {version.source} · {new Date(version.createdAt).toLocaleString()}
                  </Text>
                </Box>
                <IconButton
                  aria-label={`Restore version ${version.version}`}
                  title="Restore"
                  size="sm"
                  variant="ghost"
                  loading={restoringVersion === version.version}
                  disabled={version.version === detail?.currentVersion}
                  onClick={() => void restore(version.version)}
                >
                  <RotateCcw size={15} />
                </IconButton>
              </HStack>
            ))}
            {!detail?.versions.length ? (
              <Text fontSize="sm" color="ui.text.secondary">
                No versions yet.
              </Text>
            ) : null}
          </VStack>
        ) : detail?.current || dirty ? (
          <VStack align="stretch" gap="5" p="5">
            <Box>
              <Text fontSize="lg" fontWeight="800">
                {previewTokens?.name || 'Project design system'}
              </Text>
              {previewTokens?.description ? (
                <Text mt="1" fontSize="sm" color="ui.text.secondary">
                  {previewTokens.description}
                </Text>
              ) : null}
            </Box>
            {previewTokens?.colors.length ? (
              <Box>
                <Text mb="2" fontSize="xs" fontWeight="700" color="ui.text.tertiary">
                  COLORS
                </Text>
                <Flex wrap="wrap" gap="2">
                  {previewTokens.colors.map(color => (
                    <HStack
                      key={color.name}
                      p="2"
                      minW="150px"
                      borderWidth="1px"
                      borderColor="ui.border.subtle"
                      borderRadius="glassButton"
                      bg="ui.surface.drawer"
                    >
                      <Box
                        boxSize="28px"
                        borderRadius="md"
                        bg={color.value}
                        borderWidth="1px"
                        borderColor="blackAlpha.200"
                      />
                      <Box>
                        <Text fontSize="xs" fontWeight="700">
                          {color.name}
                        </Text>
                        <Text fontSize="2xs" color="ui.text.tertiary">
                          {color.value}
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </Flex>
              </Box>
            ) : null}
            {previewTokens?.typography.length ? (
              <Box>
                <Text mb="2" fontSize="xs" fontWeight="700" color="ui.text.tertiary">
                  TYPOGRAPHY
                </Text>
                <VStack align="stretch" gap="2">
                  {previewTokens.typography.map(type => (
                    <Box
                      key={type.name}
                      p="3"
                      borderWidth="1px"
                      borderColor="ui.border.subtle"
                      borderRadius="glassButton"
                      bg="ui.surface.drawer"
                    >
                      <Text fontSize="sm" fontWeight="700">
                        {type.name}
                      </Text>
                      <Text fontSize="xs" color="ui.text.secondary">
                        {[type.fontFamily, type.fontSize, type.fontWeight]
                          .filter(value => value !== null)
                          .join(' · ')}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : null}
            {previewMarkdown ? (
              <Box fontSize="sm">
                <SafeMarkdown>{previewMarkdown}</SafeMarkdown>
              </Box>
            ) : null}
          </VStack>
        ) : (
          <Center minH="420px" p="6">
            <VStack gap="4" maxW="390px" textAlign="center">
              <Center boxSize="52px" layerStyle="iconTile">
                <Palette size={23} />
              </Center>
              <Box>
                <Text fontSize="md" fontWeight="800">
                  Bring in your visual system
                </Text>
                <Text mt="1" fontSize="sm" color="ui.text.secondary">
                  Attune uses one project DESIGN.md to guide future website and Canvas edits.
                </Text>
              </Box>
              <HStack wrap="wrap" justify="center">
                <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={15} /> Upload
                </Button>
                <Button size="sm" variant="outline" onClick={() => void paste()}>
                  <FileCode2 size={15} /> Paste
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  loading={importing}
                  onClick={() => void importFromGitHub()}
                >
                  <Github size={15} /> Import GitHub
                </Button>
              </HStack>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setMode('source');
                  queueMicrotask(() => sourceRef.current?.focus());
                }}
              >
                Start blank
              </Button>
            </VStack>
          </Center>
        )}
      </Box>
      <Input
        ref={fileInputRef}
        type="file"
        accept=".md,text/markdown,text/plain"
        display="none"
        onChange={event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          if (dirty && !window.confirm('Discard the unsaved source and load this file?')) return;
          if (file.size > 64 * 1024) {
            toast.error('DESIGN.md must be 64 KiB or smaller');
            return;
          }
          void file.text().then(content => {
            setDraft(content);
            setSource('upload');
            setMode('source');
          });
        }}
      />
    </Flex>
  );
}

function emptyTokenSnapshot(): ProjectDesignSystemTokenSnapshot {
  return {
    name: null,
    description: null,
    colors: [],
    typography: [],
    rounded: [],
    spacing: [],
    components: [],
    sections: [],
  };
}
